class SearchModal extends HTMLElement {
  constructor() {
    super();

    this.detailsContainer = this.querySelector('details');
    this.summaryToggle = this.querySelector('summary');
    this.onSummaryClick = this.handleSummaryClick.bind(this);
    this.onSummaryFocusIn = this.handleSummaryFocusIn.bind(this);
    this.onTriggerInput = this.handleTriggerInput.bind(this);
    this.onTriggerKeyDown = this.handleTriggerKeyDown.bind(this);
    this.onTriggerCloseClick = this.handleTriggerCloseClick.bind(this);
    this.onCloseClick = this.close.bind(this);
    this.onResetClick = this.reset.bind(this);
    this.onViewportChange = this.handleViewportChange.bind(this);
    this.isClosing = false;
    this.viewportTicking = false;

    this.detailsContainer.addEventListener(
      'keyup',
      (event) => event.code && event.code.toUpperCase() === 'ESCAPE' && this.close()
    );
    this.summaryToggle.addEventListener('click', this.onSummaryClick);
    this.summaryToggle.addEventListener('focusin', this.onSummaryFocusIn);

    this.desktopInput = this.querySelector('[data-search-input-desktop]');
    this.mobileInput = this.querySelector('[data-search-input-mobile]');
    this.desktopWrap = this.desktopInput?.closest('.header__search-input-wrap') || null;
    this.mobileWrap = this.mobileInput?.closest('.header__search-bar') || null;

    [this.desktopInput, this.mobileInput].forEach((input) => {
      if (!input) return;
      input.addEventListener('input', this.onTriggerInput);
      input.addEventListener('keydown', this.onTriggerKeyDown);
    });

    // Mit AI-Suche fuehrt jedes Suchfeld im Header ins AI-Overlay, auch wenn es
    // per Tastatur/Tab oder programmatisch den Fokus bekommt. Sonst oeffnete
    // Tippen dort das alte Such-Fenster.
    if (this.hasAttribute('data-ai-search-open')) {
      [this.desktopInput, this.mobileInput].forEach((input) => {
        if (!input) return;
        input.addEventListener('focus', () => {
          const overlay = document.querySelector('ai-search-overlay');
          if (!overlay || typeof overlay.open !== 'function') return;
          const value = input.value.trim();
          input.blur();
          overlay.open(value);
        });
      });
    }

    this.triggerCloses = Array.from(this.querySelectorAll('[data-search-close]'));
    this.triggerCloses.forEach((btn) => btn.addEventListener('click', this.onTriggerCloseClick));

    const closeButton = this.querySelector('button[type="button"]');
    if (closeButton) closeButton.addEventListener('click', this.onCloseClick);

    const resetButton = this.querySelector('button[type="reset"]');
    if (resetButton) resetButton.addEventListener('click', this.onResetClick);

    this.summaryToggle.setAttribute('role', 'button');
  }

  connectedCallback() {
    // Mobile search bar is always visible; ensure it sits right under the header.
    if (this.mobileInput) {
      // Mobile input is visible by default (unless explicitly hidden by user action).
      if (!this.hasAttribute('data-mobile-hidden')) {
        this.removeAttribute('data-mobile-hidden');
      }
      this.ensureHeaderBottomPosition();
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange);
    }
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onViewportChange);
    window.removeEventListener('resize', this.onViewportChange);
  }

  handleViewportChange() {
    if (!this.mobileInput) return;
    if (this.viewportTicking) return;
    this.viewportTicking = true;
    requestAnimationFrame(() => {
      this.ensureHeaderBottomPosition();
      this.viewportTicking = false;
    });
  }

  ensureHeaderBottomPosition() {
    // Prefer theme utility, but provide a local fallback so CSS `top: var(--header-bottom-position)`
    // always has a value when the modal opens on mobile.
    if (typeof setHeaderBottomPosition === 'function') setHeaderBottomPosition();

    // Desktop: set positions relative to header heading/menu for precise anchoring.
    const isDesktop =
      typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 990px)').matches;
    if (isDesktop) {
      const heading = document.querySelector('[data-header-logo]');
      if (heading) {
        document.documentElement.style.setProperty(
          '--header-heading-bottom-position',
          `${Math.round(heading.getBoundingClientRect().bottom)}px`
        );
      }

      const inlineMenu =
        document.querySelector('.header__inline-menu') ||
        document.querySelector('.header-inline-menu') ||
        document.querySelector('.header__menu');
      if (inlineMenu) {
        document.documentElement.style.setProperty(
          '--header-inline-menu-top-position',
          `${Math.round(inlineMenu.getBoundingClientRect().top)}px`
        );
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${Math.round(inlineMenu.getBoundingClientRect().bottom)}px`
        );
        return;
      }
    }

    const current = getComputedStyle(document.documentElement).getPropertyValue('--header-bottom-position').trim();
    if (current) return;

    const header =
      this.closest('.header-wrapper') ||
      this.closest('.shopify-section-header') ||
      document.querySelector('.shopify-section-header') ||
      document.querySelector('.header-wrapper');
    if (!header) return;

    document.documentElement.style.setProperty('--header-bottom-position', `${Math.round(header.getBoundingClientRect().bottom)}px`);
  }

  handleSummaryClick(event) {
    event.preventDefault();

    // Ist die AI-Suche an (data-ai-search-open), oeffnet sie ihr eigenes Overlay
    // ueber einen Klick-Listener am document. Frueher oeffnete dieser Handler
    // trotzdem das alte Such-Fenster mit, darunter oder darueber. Schloss man
    // eines der beiden, blieb das andere samt dunklem Hintergrund stehen.
    if (this.hasAttribute('data-ai-search-open') && document.querySelector('ai-search-overlay')) return;
    const target = event.target instanceof Element ? event.target : null;
    const isInputClick = !!target?.closest('[data-search-input-desktop],[data-search-input-mobile]');
    const isOpen = this.detailsContainer.hasAttribute('open');
    const isMobile = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 749px)').matches;

    // Header input should behave like a normal input:
    // - click never closes the modal
    // - click does NOT open the modal (opening happens on typing)
    if (isInputClick) {
      return;
    }

    // Mobile: search icon opens the input bar
    if (isMobile && this.mobileInput) {
      this.removeAttribute('data-mobile-hidden');
      this.ensureHeaderBottomPosition();
      this.mobileInput.focus();
      return;
    }

    isOpen ? this.close() : this.open(event);
  }

  handleSummaryFocusIn(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (!target.hasAttribute('data-search-trigger')) return;
    if (this.isClosing) return;
    // Desktop: don't open on focus; open on typing.
  }

  getModalInput() {
    return this.detailsContainer.querySelector('.search-modal input[type="search"]');
  }

  handleTriggerInput(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    const value = target.value || '';
    const hasQuery = value.trim().length > 0;

    if (target.matches('[data-search-input-mobile]')) {
      this.removeAttribute('data-mobile-hidden');
    }

    const wrap = target.closest('.header__search-input-wrap') || target.closest('.header__search-bar');
    if (wrap) {
      if (hasQuery) wrap.setAttribute('data-has-value', '');
      else wrap.removeAttribute('data-has-value');
    }

    if (!hasQuery) {
      if (this.detailsContainer.hasAttribute('open')) this.close();
      return;
    }

    if (!this.detailsContainer.hasAttribute('open')) this.open();

    const modalInput = this.getModalInput();
    if (!modalInput) return;

    if (modalInput.value !== value) {
      modalInput.value = value;
      modalInput.dispatchEvent(new Event('input', { bubbles: true }));
      modalInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  handleTriggerKeyDown(event) {
    if (!(event instanceof KeyboardEvent)) return;
    if (event.key !== 'Enter') return;

    const form = this.detailsContainer.querySelector('.search-modal form[action]');
    if (!form) return;

    event.preventDefault();
    form.submit();
  }

  handleTriggerCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();

    const isMobile = typeof window.matchMedia === 'function' && window.matchMedia('(max-width: 749px)').matches;

    // Mobile: das X blendet die Leiste aus, bis zum naechsten Seitenaufruf.
    // Offene Ergebnisse gehen dabei mit zu. Frueher schloss das X bei offenem
    // details nur die Ergebnisse und setzte den Fokus zurueck ins Feld; war das
    // details unbemerkt offen (Ergebnisse weggescrollt), sah das X aus, als
    // oeffne es die Suche statt sie zu schliessen.
    if (isMobile && this.mobileInput) {
      if (this.detailsContainer.hasAttribute('open')) this.close();

      this.setAttribute('data-mobile-hidden', '');

      const modalInput = this.getModalInput();
      if (modalInput) {
        modalInput.value = '';
        modalInput.dispatchEvent(new Event('input', { bubbles: true }));
        modalInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.mobileInput.value = '';
      if (this.mobileWrap) this.mobileWrap.removeAttribute('data-has-value');
      this.mobileInput.blur();
      return;
    }

    // Desktop: clear + close
    const modalInput = this.getModalInput();
    if (modalInput) {
      modalInput.value = '';
      modalInput.dispatchEvent(new Event('input', { bubbles: true }));
      modalInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (this.desktopInput) this.desktopInput.value = '';
    if (this.desktopWrap) this.desktopWrap.removeAttribute('data-has-value');

    this.close();

    if (this.desktopInput) this.desktopInput.focus();
  }

  onBodyClick(event) {
    // Desktop header search: close only via explicit close button
    if (this.desktopInput || this.mobileInput) return;

    if (!this.contains(event.target) || event.target.classList.contains('modal-overlay')) this.close();
  }

  open() {
    setScrollbarWidth();
    this.ensureHeaderBottomPosition();

    this.onBodyClickEvent = this.onBodyClickEvent || this.onBodyClick.bind(this);

    this.detailsContainer.setAttribute('open', true);
    document.body.addEventListener('click', this.onBodyClickEvent);
    document.body.classList.add('search-modal--open');

    trapFocus(
      this.detailsContainer.querySelector('[tabindex="-1"]'),
      this.desktopInput || this.mobileInput || this.detailsContainer.querySelector('input:not([type="hidden"])')
    );
  }

  close() {
    this.isClosing = true;
    removeTrapFocus();
    this.detailsContainer.removeAttribute('open');
    // Ensure <details> closed even if attribute state got out of sync
    this.detailsContainer.open = false;
    document.body.removeEventListener('click', this.onBodyClickEvent);
    document.body.classList.remove('search-modal--open');
    document.body.classList.remove('predictive-search--focus');
    document.dispatchEvent(new CustomEvent('searchmodal:close'));

    // Prevent immediate re-open when the trigger still has focus.
    requestAnimationFrame(() => {
      this.isClosing = false;
    });
  }

  reset(event) {
    event.preventDefault();
    const modalInput = this.getModalInput();
    if (modalInput) modalInput.value = '';
    if (this.desktopInput) this.desktopInput.value = '';
    if (this.mobileInput) this.mobileInput.value = '';
    if (this.desktopWrap) this.desktopWrap.removeAttribute('data-has-value');
    if (this.mobileWrap) this.mobileWrap.removeAttribute('data-has-value');
  }
}

if (!customElements.get('search-modal')) {
  customElements.define('search-modal', SearchModal);
}
